import { Link } from 'react-router';

import { useStandardApiListRequest, useStandardApiRequest } from 'my-core/hooks';
import { coursesPath } from 'my-core/routes';

import CourseCard from 'my-components/CourseCard';

import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { fetchCoursesApi } from 'my-actions/CourseActions';
import { addUserCourseEnrollment } from 'my-actions/UserCourseActions';

const classes = {
  comingSoon: ({ breakpoints, spacing }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing(3),
    padding: spacing(5, 2),
    textAlign: 'center',

    [breakpoints.up('sm')]: { padding: spacing(5) },
  }),
  courses: ({ spacing }) => ({
    display: 'flex',
    flexFlow: 'wrap',
    justifyContent: 'center',
    margin: spacing(2, 0),
    gap: spacing(1, 2),

    '& > *': { flex: '1 0 310px' },
  }),
};

export default function StudentCoursePageEmpty({ course, userCourse }) {
  const { school } = course;
  const {
    fetching,
    parentRequestStatus,
    results: courses,
  } = useStandardApiListRequest({
    actionCreator: fetchCoursesApi,
    parentRequestKey: `fetch_course_page_${course.id}_other_courses`,
    storeKey: 'courses',
    params: {
      fields: {
        course: ['code', 'name', 'slug', 'subject', 'stats', 'semester', 'school'],
        school: ['slug', 'name', 'short_name'],
      },
      include: ['school'],
      filter: {
        hp_featured_or_public_ocs: true,
        school_with_ancestors_id: course.school_id,
        verified: true,
        exclude_ids: course.id,
      },
      order_by: 'school_id_filter',
      order: 'desc',
    },
    limit: 12,
  });

  // add course
  const { performRequest: performAddCourse, requestStatus: addCourseStatus } = useStandardApiRequest({
    actionCreator: addUserCourseEnrollment,
    errorMessage: true,
    successMessage: 'Course successfully added to your course list',
  });

  return (
    <>
      <Paper css={classes.comingSoon}>
        <Typography variant="h5">Course Content Coming Soon</Typography>
        {userCourse ?
          <Typography>
            We haven’t yet released the schedule for this course, but we’ll let you know when we do!
          </Typography>
        : <>
            <Typography>
              We haven’t yet released the schedule for this course. To be notified when live or on-demand content is
              released, add it to your course dashboard.
            </Typography>
            <Button
              color="primary"
              disabled={!!addCourseStatus?.fetching}
              onClick={() => performAddCourse(course.id)}
              startIcon={<FontAwesomeIcon icon={faPlus} />}
              variant="contained"
            >
              Add Course
            </Button>
          </>
        }
      </Paper>
      <Typography sx={{ mt: 3 }} variant="h5">
        Related Courses at {school.short_name || school.name}
      </Typography>
      <div css={classes.courses}>
        {fetching ?
          [...Array(12)].map((_, i) => <CourseCard key={i} loading />)
        : courses.map(c => <CourseCard key={c.id} course={c} origSchoolId={course.school_id} toInCourseUX />)}
      </div>
      <div style={{ textAlign: 'center' }}>
        <Button
          component={Link}
          nativeButton={false}
          size="large"
          to={coursesPath({
            school_type: school?.type || undefined,
            ctry: school?.country_code || undefined,
            reg: school?.region_code || undefined,
            ...(course?.featured ? null : { sid: school?.id, sname: school?.short_name || school?.name }),
          })}
          variant="contained"
        >
          Browse all {parentRequestStatus?.total + 1 || '...'} courses at {school?.short_name || school?.name}
        </Button>
      </div>
    </>
  );
}
